import React from "react";
import { TextField, Button, Select, MenuItem } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { push, ref, getDatabase } from "firebase/database";

const generateId = () => push(ref(getDatabase(), "temp")).key;

const PayoutSetup = ({ formData, setFormData }) => {
    const handlePayoutNameChange = (value) => {
        setFormData((prev) => ({ ...prev, payoutSetName: value }));
    };

    const handleGroupChange = (index, field, value) => {
        const newGroups = [...(formData.payoutGroups || [])];
        newGroups[index][field] = value;
        setFormData((prev) => ({ ...prev, payoutGroups: newGroups }));
    };

    const handlePayoutItemChange = (groupIndex, itemIndex, field, value) => {
        const newGroups = [...(formData.payoutGroups || [])];
        newGroups[groupIndex].items[itemIndex][field] = value;
        setFormData((prev) => ({ ...prev, payoutGroups: newGroups }));
    };

    const addPayoutGroup = () => {
        setFormData((prev) => ({
            ...prev,
            payoutGroups: [...(prev.payoutGroups || []), { name: "", items: [] }]
        }));
    };

    const addPayoutItem = (groupIndex) => {
        const newGroups = [...(formData.payoutGroups || [])];
        newGroups[groupIndex].items.push({ id: generateId(), name: "", amount: "", type: "Net", tieBehavior: "split", priority: newGroups[groupIndex].items.length });
        setFormData((prev) => ({ ...prev, payoutGroups: newGroups }));
    };

    const addOtherPayout = () => {
        setFormData((prev) => ({
            ...prev,
            otherPayouts: [...(prev.otherPayouts || []), { id: generateId(), name: "", amount: "", holeBound: false }]
        }));
    };

    return (
        <div className="form-container">
            <h2>Payout Setup</h2>
            <TextField
                label="Payout Set Name"
                value={formData.payoutSetName || ""}
                onChange={(e) => handlePayoutNameChange(e.target.value)}
                fullWidth
                margin="normal"
            />
            <h3>Date Range</h3>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                    label="Start Date"
                    value={formData.payoutStartDate ? dayjs(formData.payoutStartDate) : null}
                    onChange={(date) => setFormData((prev) => ({ ...prev, payoutStartDate: date ? date.toDate() : null }))}
                    renderInput={(params) => <TextField {...params} className="styled-select" />}
                />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                    label="End Date"
                    value={formData.payoutEndDate ? dayjs(formData.payoutEndDate) : null}
                    onChange={(date) => setFormData((prev) => ({ ...prev, payoutEndDate: date ? date.toDate() : null }))}
                    renderInput={(params) => <TextField {...params} className="styled-select" />}
                />
            </LocalizationProvider>
            {formData.payoutGroups?.map((group, groupIndex) => (
                <div key={groupIndex} className="payout-group">
                    <TextField
                        label="Group Name"
                        value={group.name}
                        onChange={(e) => handleGroupChange(groupIndex, "name", e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    {group.items.map((item, itemIndex) => (
                        <div key={item.id} className="payout-item">
                            <TextField
                                label="Payout Item"
                                value={item.name}
                                onChange={(e) => handlePayoutItemChange(groupIndex, itemIndex, "name", e.target.value)}
                                margin="normal"
                            />
                            <TextField
                                label="Amount"
                                type="number"
                                value={item.amount}
                                onChange={(e) => handlePayoutItemChange(groupIndex, itemIndex, "amount", e.target.value)}
                                margin="normal"
                            />
                            <Select
                                value={item.type}
                                onChange={(e) => handlePayoutItemChange(groupIndex, itemIndex, "type", e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="Net">Net</MenuItem>
                                <MenuItem value="Gross">Gross</MenuItem>
                            </Select>
                            <Select
                                value={item.tieBehavior}
                                onChange={(e) => handlePayoutItemChange(groupIndex, itemIndex, "tieBehavior", e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="split">Split Money Between Players</MenuItem>
                                <MenuItem value="payAll">Pay All Players Full Amount</MenuItem>
                                <MenuItem value="payNone">Dont Pay Ties</MenuItem>
                            </Select>
                        </div>
                    ))}
                    <Button onClick={() => addPayoutItem(groupIndex)} variant={'contained'}>Add Payout Item</Button>
                </div>
            ))}
            <Button onClick={addPayoutGroup} variant={'contained'}>Add Payout Group</Button>
            <h3>Other Payouts</h3>
            {formData.otherPayouts?.map((payout) => (
                <div key={payout.id} className="other-payout">
                    <TextField
                        label="Payout Name"
                        value={payout.name}
                        onChange={(e) => {
                            const newOtherPayouts = [...formData.otherPayouts];
                            newOtherPayouts.find(p => p.id === payout.id).name = e.target.value;
                            setFormData((prev) => ({ ...prev, otherPayouts: newOtherPayouts }));
                        }}
                        margin="normal"
                    />
                    <TextField
                        label="Amount"
                        type="number"
                        value={payout.amount}
                        onChange={(e) => {
                            const newOtherPayouts = [...formData.otherPayouts];
                            newOtherPayouts.find(p => p.id === payout.id).amount = e.target.value;
                            setFormData((prev) => ({ ...prev, otherPayouts: newOtherPayouts }));
                        }}
                        margin="normal"
                    />
                    <label>
                        Hole Bound
                        <input
                            type="checkbox"
                            checked={payout.holeBound}
                            onChange={(e) => {
                                const newOtherPayouts = [...formData.otherPayouts];
                                newOtherPayouts.find(p => p.id === payout.id).holeBound = e.target.checked;
                                setFormData((prev) => ({ ...prev, otherPayouts: newOtherPayouts }));
                            }}
                        />
                    </label>
                </div>
            ))}
            <Button onClick={addOtherPayout} variant={'contained'}>Add Other Payout</Button>
        </div>
    );
};

export default PayoutSetup;